import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  Gavel as DecisionIcon,
  People as BriefingIcon,
  MenuBook as ReadingIcon,
  Chat as FollowUpIcon,
  RateReview as ReviewIcon,
  Edit as DraftIcon,
  TaskAlt as TaskIcon,
  TrendingUp as LowEnergyIcon,
} from '@mui/icons-material';

import type { WorkItem } from '../../types/models';

// ---------------------------------------------------------------------------
// WorkItemTypeTreatment — §5.5 from the improvement doc.
// "A Decision should not look like a Task. A Briefing should show who is
// briefed and when." This component gives each of the seven work item types
// a distinct icon + inline secondary hint so the eye can sort by type at
// glance — without redesigning every surface that shows work items.
//
// Two exports:
//   - WorkItemTypeBadge: small icon + label pair, drop-in next to any title
//   - workItemTypeHint:  pure function returning the secondary line text,
//                         so callers can render it in their own layout
// ---------------------------------------------------------------------------

const TYPE_META = {
  Task:     { Icon: TaskIcon,     colour: '#6C9BFF', label: 'Task' },
  Decision: { Icon: DecisionIcon, colour: '#FF8A65', label: 'Decision' },
  Briefing: { Icon: BriefingIcon, colour: '#69F0AE', label: 'Briefing' },
  Review:   { Icon: ReviewIcon,   colour: '#FFB74D', label: 'Review' },
  FollowUp: { Icon: FollowUpIcon, colour: '#CE93D8', label: 'Follow-up' },
  Draft:    { Icon: DraftIcon,    colour: '#4DD0E1', label: 'Draft' },
  Reading:  { Icon: ReadingIcon,  colour: '#AED581', label: 'Reading' },
} as const;

interface BadgeProps {
  type: WorkItem['type'];
  /** Compact = icon only with tooltip; full = icon + label chip. */
  variant?: 'compact' | 'full';
}

export function WorkItemTypeBadge({ type, variant = 'full' }: BadgeProps) {
  const meta = TYPE_META[type];
  if (!meta) return null;
  const { Icon, colour, label } = meta;

  if (variant === 'compact') {
    return (
      <Tooltip title={label} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: `${colour}22`,
            color: colour,
          }}
        >
          <Icon sx={{ fontSize: 14 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Chip
      icon={<Icon sx={{ fontSize: 14, color: `${colour} !important` }} />}
      label={label}
      size="small"
      sx={{
        bgcolor: `${colour}1F`,
        color: colour,
        fontWeight: 600,
        fontSize: '0.72rem',
        height: 22,
        '& .MuiChip-icon': { ml: 0.5 },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-type hint — short phrase appended next to the title, distinct to each
// type. Deliberately terse so it doesn't crowd a dense list row.
// ---------------------------------------------------------------------------

export function workItemTypeHint(item: WorkItem): string | null {
  switch (item.type) {
    case 'Briefing':
      // Briefing shows whoever is being briefed — the exec or a named peer.
      if (item.assignedToExecutiveId) return 'Briefing for an executive';
      return 'Briefing pack';
    case 'Decision':
      // Decisions get anchored to a driver — surface it when present.
      return item.strategicDriver?.title
        ? `Decision · aligned to ${item.strategicDriver.title}`
        : 'Decision';
    case 'Reading':
      // Reading defaults to low-energy windows (per the recommendation).
      return `Reading · fits a low-energy block (${item.estimatedMinutes} min)`;
    case 'FollowUp':
      // Who's being chased? Use the first note line if present, else a
      // neutral hint. Notes are free text so we clamp to the first line.
      {
        const firstLine = (item.notes ?? '').split('\n')[0]?.trim();
        if (firstLine && firstLine.length <= 80) return `Follow-up · ${firstLine}`;
        return 'Follow-up chase';
      }
    case 'Review':
      return 'Review';
    case 'Draft':
      return 'Draft';
    case 'Task':
    default:
      return null;
  }
}

interface InlineProps {
  item: WorkItem;
  /** Show the compact icon badge + hint text in a single row. */
  showBadge?: boolean;
}

/**
 * Convenience wrapper: `[icon-badge]  Hint text`
 * — used by list rows to give each type a visible treatment with minimal
 * per-screen layout changes.
 */
export function WorkItemTypeInline({ item, showBadge = true }: InlineProps) {
  const hint = workItemTypeHint(item);
  if (!hint && !showBadge) return null;

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" component="span">
      {showBadge && <WorkItemTypeBadge type={item.type} variant="compact" />}
      {hint && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {hint}
        </Typography>
      )}
      {item.type === 'Reading' && (
        <Tooltip title="Best scheduled in an afternoon or late-day block" arrow>
          <LowEnergyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
        </Tooltip>
      )}
    </Stack>
  );
}

export default WorkItemTypeBadge;
